import type {
  Customer,
  Supplier,
  Product,
  DashboardStats,
  ProfitTrend,
  SalesOrder,
  NavItem,
  Role,
  Driver,
} from '@/types';
import { KEYS, DATA_VERSION, dbGet, dbSet } from '@/lib/db';

// ===== 数据初始化 =====
// 数据源：仅从本机 IndexedDB 读取；全新设备登录后由认证同步接口恢复。
// 页面通过 ES module live binding 读取 MOCK_*，初始化完成后自动拿到最新数据。
export let MOCK_CUSTOMERS: Customer[] = [];
export let MOCK_SUPPLIERS: Supplier[] = [];
export let MOCK_PRODUCTS: Product[] = [];
export let MOCK_SALES: SalesOrder[] = [];

export function setMockCustomers(list: Customer[]) { MOCK_CUSTOMERS = list; }
export function setMockSuppliers(list: Supplier[]) { MOCK_SUPPLIERS = list; }
export function setMockProducts(list: Product[]) { MOCK_PRODUCTS = list; }
export function setMockSales(list: SalesOrder[]) { MOCK_SALES = list; }

// 历史数据修复：商品 brand 字段曾被错误地填入供应商名称。
// 该函数在每次启动时幂等执行：若 brand 与某个供应商名称一致，
// 则将 brand 迁移到 supplierName（仅当 supplierName 缺失），并清空 brand。
// #762 加强：无论 supplierName 是否已有值，brand 命中供应商名一律视为脏值清空，
// 避免「双字段都填了供应商名」的记录漏修。
function repairProductBrandSupplier(products: Product[], suppliers: Supplier[]): Product[] {
  const supplierNames = new Set(suppliers.map((s) => s.name).filter(Boolean));
  let changed = false;
  const next = products.map((p) => {
    if (p.brand && supplierNames.has(p.brand)) {
      changed = true;
      return { ...p, supplierName: p.supplierName || p.brand, brand: '' };
    }
    return p;
  });
  return changed ? next : products;
}

/**
 * 历史数据修复（#824）：供应商「经营类别」曾被填入品牌列表
 * （如 G006 益顺行香厂的 category="全发行，建新行，益顺行，长源行"）。
 * 启发式（保守，避免误伤合法类目）：brand 为空 && category 为逗号分隔的
 * >=2 个 token && 每个 token 以「行/厂」结尾（香行/香厂品牌名特征）→ 迁移到
 * brand，category 置空。幂等；BasicDataPage 导入时也复用同一判定防复现。
 */
export function normalizeSupplierBrandCategory<T extends { brand?: string; category: string }>(s: T): T {
  const cat = (s.category || '').trim();
  if (!cat || (s.brand || '').trim()) return s;
  const tokens = cat.split(/[，,、]/).map((t) => t.trim()).filter(Boolean);
  if (tokens.length < 2) return s;
  if (!tokens.every((t) => t.length >= 2 && /[行厂]$/.test(t))) return s;
  return { ...s, brand: tokens.join('，'), category: '' };
}

export function repairSupplierBrandCategory(suppliers: Supplier[]): Supplier[] {
  let changed = false;
  const next = suppliers.map((s) => {
    const r = normalizeSupplierBrandCategory(s);
    if (r !== s) changed = true;
    return r;
  });
  return changed ? next : suppliers;
}

/**
 * #824 运行期修复：与 repairProductBrandSupplierNow 同一模式——启动全量拉取
 * 用服务器历史脏数据覆盖本地后，在 syncEngine 拉取应用了 suppliers 域之后
 * 调用本函数：修复本地 IndexedDB + dbSet 触发写入钩子（3s 防抖推送）把
 * 干净数据回推服务器，多台设备逐台自愈。幂等，修完即无操作。
 */
export async function repairSupplierBrandCategoryNow(): Promise<boolean> {
  try {
    const s = await dbGet<Supplier[]>(KEYS.suppliers);
    if (!s?.length) return false;
    const repaired = repairSupplierBrandCategory(s);
    if (repaired === s) return false;
    setMockSuppliers(repaired);
    await dbSet(KEYS.suppliers, repaired);
    return true;
  } catch {
    return false;
  }
}

/**
 * 期初快照修复（#815）：存量商品规格大多没有 initialStock（期初数），
 * 导致安全库存阈值（缺省按期初×20%）无法派生。本函数幂等补全：
 * initialStock === undefined 时补 initialStock = stock（当前库存快照），
 * 已有值（含 0）一律不动。与 repairProductBrandSupplier 同属启动/同步后自愈链。
 */
export function repairSpecThresholds(products: Product[]): Product[] {
  let changed = false;
  const next = products.map((p) => {
    let specChanged = false;
    const specs = p.specs.map((sp) => {
      if (sp.initialStock === undefined) {
        specChanged = true;
        return { ...sp, initialStock: sp.stock };
      }
      return sp;
    });
    if (!specChanged) return p;
    changed = true;
    return { ...p, specs };
  });
  return changed ? next : products;
}

/**
 * #762 运行期修复：启动全量拉取（#715，每次打开页面 cursor=0）会用服务器
 * sync_records 里的历史脏数据覆盖本地——而 initMockData 里的修复只跑一次，
 * 时序上在拉取之前，等于白修（实测服务器 4788 条商品记录中 4266 条
 * brand 仍等于供应商名）。
 * 本函数在同步应用了 products/suppliers 域之后调用：修复本地 IndexedDB +
 * dbSet 触发写入钩子（onDbWrite → notifyWrite → 3s 防抖推送），把干净数据
 * 回推服务器；多台设备逐台自愈，服务器脏记录被 LWW 覆盖后全网收敛。幂等。
 */
export async function repairProductBrandSupplierNow(): Promise<boolean> {
  try {
    const [s, p] = await Promise.all([
      dbGet<Supplier[]>(KEYS.suppliers),
      dbGet<Product[]>(KEYS.products),
    ]);
    if (!s?.length || !p?.length) return false;
    const repaired = repairSpecThresholds(repairProductBrandSupplier(p, s));
    if (repaired === p) return false;
    setMockProducts(repaired);
    await dbSet(KEYS.products, repaired);
    return true;
  } catch {
    return false;
  }
}

// 应用启动时调用：优先 IndexedDB，无则 fetch JSON 并缓存
export async function initMockData(): Promise<void> {
  const storedVersion = await dbGet<string>(KEYS.version);

  // 版本一致且 IndexedDB 已有数据 → 直接使用
  if (storedVersion === DATA_VERSION) {
    const [c, s, p, o] = await Promise.all([
      dbGet<Customer[]>(KEYS.customers),
      dbGet<Supplier[]>(KEYS.suppliers),
      dbGet<Product[]>(KEYS.products),
      dbGet<SalesOrder[]>(KEYS.salesOrders),
    ]);
    if (s?.length && p?.length) {
      const repaired = repairSpecThresholds(repairProductBrandSupplier(p, s));
      if (repaired !== p) {
        setMockProducts(repaired);
        await dbSet(KEYS.products, repaired).catch(() => {});
      } else if (p?.length) {
        setMockProducts(p);
      }
    } else if (p?.length) {
      setMockProducts(p);
    }
    if (o?.length) setMockSales(o);
    if (c?.length) setMockCustomers(c);
    if (s?.length) {
      const repairedSuppliers = repairSupplierBrandCategory(s);
      setMockSuppliers(repairedSuppliers);
      if (repairedSuppliers !== s) await dbSet(KEYS.suppliers, repairedSuppliers).catch(() => {});
    }
    return;
  }

  // 版本不一致时优先保留用户已有数据，不从公开静态目录导入业务数据。
  const [existingC, existingS, existingP, existingO] = await Promise.all([
    dbGet<Customer[]>(KEYS.customers),
    dbGet<Supplier[]>(KEYS.suppliers),
    dbGet<Product[]>(KEYS.products),
    dbGet<SalesOrder[]>(KEYS.salesOrders),
  ]);

  const hasAnyUserData = Boolean(
    existingC?.length || existingS?.length || existingP?.length || existingO?.length
  );

  if (hasAnyUserData) {
    // 保留现有数据，仅升级版本号，避免再次触发迁移
    if (existingC?.length) setMockCustomers(existingC);
    if (existingS?.length) {
      const repairedSuppliers = repairSupplierBrandCategory(existingS);
      setMockSuppliers(repairedSuppliers);
      if (repairedSuppliers !== existingS) await dbSet(KEYS.suppliers, repairedSuppliers).catch(() => {});
    }
    if (existingP?.length) {
      const repaired = repairSpecThresholds(existingS?.length
        ? repairProductBrandSupplier(existingP, existingS)
        : existingP);
      setMockProducts(repaired);
      if (repaired !== existingP) await dbSet(KEYS.products, repaired).catch(() => {});
    }
    if (existingO?.length) setMockSales(existingO);
    await dbSet(KEYS.version, DATA_VERSION);
    return;
  }

  // 全新设备不再从公开静态 JSON 装载业务数据。登录成功后 triggerFullSync()
  // 会通过 Bearer 认证拉取服务器快照，避免客户和订单数据出现在网页目录。
  await dbSet(KEYS.version, DATA_VERSION);
}

// ===== 角色定义 =====
export const ROLES: Record<string, Role> = {
  boss: { type: 'boss', label: '老板', color: 'text-red-600', bgColor: 'bg-red-50' },
  manager: { type: 'manager', label: '经理', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  sales: { type: 'sales', label: '业务员', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  retail: { type: 'retail', label: '零售员', color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  driver: { type: 'driver', label: '司机', color: 'text-green-600', bgColor: 'bg-green-50' },
  writer: { type: 'writer', label: '写单员', color: 'text-teal-600', bgColor: 'bg-teal-50' },
  shopkeeper: { type: 'shopkeeper', label: '店面', color: 'text-orange-600', bgColor: 'bg-orange-50' },
};

// ===== 导航菜单（二级菜单结构）=====
export const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: '工作台', icon: 'LayoutDashboard', path: 'dashboard' },
  { id: 'marketing', label: '营销中心', icon: 'Sparkles', path: 'festival-promotion', children: [
    { id: 'festival-promotion', label: '节日促销', icon: 'Tag', path: 'festival-promotion' },
  ] },
  {
    id: 'basic-data', label: '基础数据', icon: 'Database', path: 'basic-data',
    children: [
      { id: 'basic-data-customers', label: '客户档案', icon: 'Users', path: 'basic-data' },
      { id: 'basic-data-suppliers', label: '供应商', icon: 'Truck', path: 'basic-data' },
      { id: 'basic-data-warehouses', label: '仓库管理', icon: 'Warehouse', path: 'basic-data' },
      { id: 'basic-data-products', label: '商品资料', icon: 'Package', path: 'basic-data' },
      { id: 'basic-data-festnotes', label: '节日小纸条', icon: 'StickyNote', path: 'basic-data' },
      { id: 'basic-data-todo', label: '待办事项', icon: 'ClipboardList', path: 'todo' },
    ],
  },
  {
    id: 'purchase', label: '进货管理', icon: 'Truck', path: 'purchase',
    children: [
      { id: 'purchase-po', label: '在途采购', icon: 'ClipboardList', path: 'purchase' },
      { id: 'purchase-checking', label: '清点验收', icon: 'PackageCheck', path: 'purchase' },
      { id: 'purchase-inbound', label: '已入库', icon: 'PackagePlus', path: 'purchase' },
      { id: 'purchase-payable', label: '应付款', icon: 'Wallet', path: 'purchase' },
      { id: 'purchase-return', label: '供应商退货', icon: 'Undo2', path: 'purchase' },
      { id: 'purchase-detail', label: '进货明细', icon: 'ListOrdered', path: 'purchase' },
      { id: 'purchase-history', label: '供应商历史', icon: 'History', path: 'purchase' },
    ],
  },
  {
    id: 'inventory', label: '库存管理', icon: 'Package', path: 'inventory',
    children: [
      { id: 'inventory-stock', label: '库存查询', icon: 'Search', path: 'inventory' },
      { id: 'inventory-batch', label: '批次管理', icon: 'Layers', path: 'inventory' },
      { id: 'inventory-outbound', label: '出库单', icon: 'PackageMinus', path: 'inventory' },
      { id: 'inventory-warning', label: '库存预警', icon: 'AlertTriangle', path: 'inventory' },
    ],
  },
  {
    id: 'sales', label: '销售管理', icon: 'ShoppingCart', path: 'sales',
    children: [
      { id: 'sales-quotation', label: '报价单', icon: 'FileText', path: 'quotation' },
      { id: 'sales-pending', label: '待出库', icon: 'ClipboardList', path: 'sales' },
      { id: 'sales-delivering', label: '配送中', icon: 'Truck', path: 'sales' },
      { id: 'sales-order', label: '销售单', icon: 'Receipt', path: 'sales' },
      { id: 'sales-product-details', label: '商品销售明细', icon: 'ListOrdered', path: 'sales' },
      { id: 'sales-receivables', label: '应收款', icon: 'Wallet', path: 'sales' },
      { id: 'sales-return', label: '退货记录', icon: 'Undo2', path: 'sales' },
    ],
  },
  { id: 'retail', label: '零售收银', icon: 'ScanLine', path: 'retail' },
  {
    id: 'finance', label: '财务管理', icon: 'Wallet', path: 'finance',
    children: [
      { id: 'finance-receivable', label: '应收账款', icon: 'TrendingUp', path: 'finance' },
      { id: 'finance-payable', label: '应付账款', icon: 'TrendingDown', path: 'finance' },
      { id: 'finance-reconcile', label: '对账中心', icon: 'Scale', path: 'finance' },
    ],
  },
  {
    id: 'map', label: '地图配送', icon: 'MapPin', path: 'map',
    children: [
      { id: 'map-customers', label: '客户分布', icon: 'Map', path: 'map' },
      { id: 'map-addresses', label: '地址收藏', icon: 'MapPinned', path: 'map' },
      { id: 'map-drivers', label: '司机位置', icon: 'Truck', path: 'map' },
      { id: 'map-route', label: '路线规划', icon: 'Route', path: 'map' },
    ],
  },
  {
    id: 'barcode', label: '打印设置', icon: 'Barcode', path: 'barcode',
    children: [
      { id: 'barcode-generate', label: '条码生成', icon: 'Barcode', path: 'barcode' },
      { id: 'barcode-label', label: '标签模板', icon: 'Tag', path: 'barcode' },
      { id: 'barcode-delivery', label: '送货单模板', icon: 'FileText', path: 'barcode' },
      { id: 'barcode-store', label: '店内打印', icon: 'Printer', path: 'barcode' },
      { id: 'barcode-cloud', label: '云打印', icon: 'Cloud', path: 'barcode' },
    ],
  },
  {
    id: 'analysis', label: '数据分析', icon: 'TrendingUp', path: 'analysis',
    children: [
      { id: 'analysis-profit', label: '利润分析', icon: 'DollarSign', path: 'analysis' },
      { id: 'analysis-product', label: '商品分析', icon: 'Package', path: 'analysis' },
      { id: 'analysis-customer', label: '客户分析', icon: 'Users', path: 'analysis' },
    ],
  },
  { id: 'ai-assistant', label: 'AI 助手', icon: 'Sparkles', path: 'ai-assistant' },
  {
    id: 'system', label: '系统管理', icon: 'Settings', path: 'system',
    children: [
      { id: 'system-users', label: '用户管理', icon: 'UserCog', path: 'system' },
      { id: 'system-role', label: '角色权限', icon: 'Shield', path: 'system' },
      { id: 'system-api', label: 'API 管理', icon: 'Zap', path: 'system' },
      { id: 'system-log', label: '操作日志', icon: 'Activity', path: 'system' },
      { id: 'system-data', label: '数据导入导出', icon: 'Database', path: 'system' },
      { id: 'system-security', label: '安全中心', icon: 'Lock', path: 'system' },
      { id: 'system-backup', label: '备份与恢复', icon: 'ArchiveRestore', path: 'system' },
      { id: 'system-sync', label: '多设备同步', icon: 'CloudCog', path: 'system' },
      { id: 'system-brand', label: '品牌设置', icon: 'ImagePlus', path: 'system' },
    ],
  },
];

// 移动端 Tab
export const MOBILE_TABS: Array<{ id: string; childId?: string; label: string; icon: string }> = [
  { id: 'dashboard', label: '工作台', icon: 'LayoutDashboard' },
  { id: 'sales', label: '销售', icon: 'ShoppingCart' },
  { id: 'basic-data', childId: 'basic-data-products', label: '商品', icon: 'Package' },
  { id: 'map', label: '地图', icon: 'MapPin' },
  { id: 'system', label: '我的', icon: 'User' },
];

// ===== Dashboard 统计 =====
export const DASHBOARD_STATS: DashboardStats = {
  todaySales: 12860,
  todayOrders: 23,
  monthProfit: 45200,
  monthProfitGrowth: 12.5,
  totalCustomers: 523,
  totalProducts: 2416,
  totalStockValue: 386000,
  unsettledReceivable: 86500,
  unsettledPayable: 42300,
};

// ===== 利润趋势 =====
export const PROFIT_TRENDS: ProfitTrend[] = [
  { month: '1月', revenue: 120000, cost: 84000, profit: 36000 },
  { month: '2月', revenue: 135000, cost: 91000, profit: 44000 },
  { month: '3月', revenue: 128000, cost: 89000, profit: 39000 },
  { month: '4月', revenue: 142000, cost: 96000, profit: 46000 },
  { month: '5月', revenue: 156000, cost: 104000, profit: 52000 },
  { month: '6月', revenue: 138000, cost: 93000, profit: 45000 },
  { month: '7月', revenue: 163000, cost: 108000, profit: 55000 },
  { month: '8月', revenue: 148000, cost: 102800, profit: 45200 },
];
export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string;
  manager: string;
  phone: string;
}
export const MOCK_WAREHOUSES: Warehouse[] = [
  { id: '1', code: 'W01', name: '1号仓·总仓', address: '东莞市石龙镇XX路1号', manager: '负责人A', phone: '13800138000' },
  { id: '2', code: 'W02', name: '2号仓·香烛仓', address: '东莞市石龙镇XX路2号', manager: '负责人B', phone: '13800138001' },
  { id: '3', code: 'W03', name: '3号仓·食品仓', address: '东莞市石龙镇XX路3号', manager: '负责人C', phone: '13800138002' },
  { id: '4', code: 'W04', name: '4号仓·日用品仓', address: '东莞市石龙镇XX路4号', manager: '负责人D', phone: '13800138003' },
  { id: '5', code: 'W05', name: '5号仓·调味品仓', address: '东莞市石龙镇XX路5号', manager: '负责人E', phone: '13800138004' },
];
export const STORE_LOCATION = {
  name: '昌发佛具（店铺）',
  address: '东莞市石龙镇昌平路昌发佛具',
  lat: 23.1042,
  lng: 113.8789,
};

// ===== 司机样例 =====
export const MOCK_DRIVERS: Driver[] = [
  { id: '1', driverId: 'D001', name: '陈师傅', phone: '13800139001', plateNo: '粤S·12345', online: true, lastOnlineTime: '2026-08-10 14:30', speed: 45, lat: 23.0207, lng: 113.7518 },
  { id: '2', driverId: 'D002', name: '刘师傅', phone: '13800139002', plateNo: '粤S·67890', online: true, lastOnlineTime: '2026-08-10 14:28', speed: 0, lat: 23.0895, lng: 114.3893 },
  { id: '3', driverId: 'D003', name: '黄师傅', phone: '13800139003', plateNo: '粤L·54321', online: false, lastOnlineTime: '2026-08-09 18:00', speed: 0 },
];

// ===== 功能宫格 =====
export const FUNCTION_GRID = [
  { id: 'quotation', label: '报价单', icon: 'FileText', color: 'bg-blue-500', path: 'quotation' },
  { id: 'sales-order', label: '销售单', icon: 'ReceiptText', color: 'bg-green-500', path: 'sales' },
  { id: 'retail', label: '零售收银', icon: 'ScanLine', color: 'bg-orange-500', path: 'retail' },
  { id: 'inbound', label: '入库单', icon: 'PackagePlus', color: 'bg-purple-500', path: 'purchase' },
  { id: 'inventory', label: '库存查询', icon: 'Search', color: 'bg-cyan-500', path: 'inventory' },
  { id: 'reconciliation', label: '对账', icon: 'Calculator', color: 'bg-pink-500', path: 'finance' },
  { id: 'barcode', label: '条码打印', icon: 'Barcode', color: 'bg-indigo-500', path: 'barcode' },
  { id: 'route', label: '送货路线', icon: 'Route', color: 'bg-teal-500', path: 'map' },
];

// ===== 待办事项 =====
export const TODO_ITEMS = [
  { id: '1', title: '华联商行待结清', amount: 12000, type: 'receivable', priority: 'high' },
  { id: '2', title: '广州白云食品待付款', amount: 12000, type: 'payable', priority: 'medium' },
  { id: '3', title: '3条库存预警', amount: 0, type: 'warning', priority: 'low' },
  { id: '4', title: '2张入库单待确认', amount: 0, type: 'task', priority: 'medium' },
];

// 格式化金额
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// 格式化数字
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('zh-CN').format(num);
}
